/* The /pst-viewer page.
 *
 * Three things this interface must never do, each learned from a measurement
 * rather than from taste:
 *
 *  1. Report the number of items a folder CLAIMS as though it were the number
 *     that can be read. On a damaged archive those differ — 71 declared, 70
 *     readable — and the gap is precisely what the person came to find out.
 *  2. Show a folder's badge and then an empty list with no explanation. Outlook
 *     search folders declare a count and hold nothing; that is the format being
 *     honest, and the interface has to pass the honesty on.
 *  3. Freeze. A damaged archive can send a parser round in circles, so every
 *     call has a deadline and a wedged worker is replaced, not waited on.
 */

import { PstSession } from './index.js'
import { reportShape } from '../file-shape.js'
import { suggestOtherTool } from '../file-signature.js'

const T = (window.__PST_I18N || {})

/**
 * Look up a string and fill its placeholders.
 *
 * The translation files use Laravel's `:name` form, because that is what every
 * other file on the site uses and what the translation tests expect. The first
 * version of this file substituted `{name}` instead and the mismatch was
 * invisible to every test — it only showed on screen, as the literal text
 * "показано :shown из :total". Screenshots are not decoration.
 */
const t = (k, fallback, vars) => {
  let s = T[k] || fallback || k
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      s = s.split(':' + name).join(String(value))
    }
  }
  return s
}
const $ = sel => document.querySelector(sel)
const el = (tag, cls, text) => {
  const n = document.createElement(tag)
  if (cls) n.className = cls
  if (text != null) n.textContent = text
  return n
}
const fmtBytes = n => {
  if (!n && n !== 0) return ''
  const u = [t('unit_b', 'B'), t('unit_kb', 'KB'), t('unit_mb', 'MB'), t('unit_gb', 'GB')]
  let i = 0
  let v = n
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v < 10 && i ? v.toFixed(1) : Math.round(v)} ${u[i]}`
}
const fmtDate = iso => {
  if (!iso) return ''
  try { return new Date(iso).toLocaleString(window.__LOCALE || undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) }
  catch { return iso.slice(0, 16).replace('T', ' ') }
}

const state = { session: null, tree: [], folderId: null, rows: [], offset: 0, total: 0, opened: null, filter: '' }

function setStatus(msg, kind = 'info') {
  const box = $('#pst-status')
  if (!box) return
  box.hidden = !msg
  box.className = 'pst-status lg-card pst-status--' + kind
  box.textContent = msg || ''
}

/** The headline: what the archive is, and what is wrong with it. */
function renderSummary(o) {
  const box = $('#pst-summary')
  box.innerHTML = ''
  box.hidden = false

  const chips = el('div', 'pst-chips')
  const chip = (label, value, kind = '') => {
    const c = el('span', 'pst-chip' + (kind ? ' pst-chip--' + kind : ''))
    c.append(el('b', null, label), document.createTextNode(' '), el('span', null, value))
    chips.append(c)
  }

  chip(t('chip_format', 'Format'), o.header.isOst ? 'OST · ' + o.header.formatLabel : 'PST · ' + o.header.formatLabel)
  chip(t('chip_size', 'Size'), fmtBytes(o.header.fileSize))
  chip(t('chip_folders', 'Folders'), String(o.totals.folders))

  /* Two numbers, never one. "Messages: 71" on an archive where 70 can be opened
     is a lie of omission, and the missing one is the reason the person is here. */
  chip(t('chip_declared', 'Items listed'), String(o.totals.messages))

  if (o.decoded) {
    chip(t('chip_encoding', 'Encoding'),
      o.decoded.relabelled
        ? t('encoding_relabelled', 'header said :a, data is :b', { a: o.decoded.declared, b: o.decoded.scheme })
        : o.decoded.scheme, 'warn')
  }
  if (o.repaired) chip(t('chip_repaired', 'Repaired'), t('repaired_yes', 'index rebuilt'), 'warn')
  box.append(chips)

  if (o.repaired && o.repair) {
    const r = o.repair
    const d = el('div', 'pst-repair-note')
    const bits = []
    if (o.openError) bits.push(t('repair_reason', 'The archive would not open: :e', { e: o.openError }))
    if (r.fromLive) bits.push(t('repair_live', ':n entries came from the surviving index', { n: r.fromLive.nodes + r.fromLive.blocks }))
    if (r.fromScan) bits.push(t('repair_scan', ':n more were found by scanning :p orphaned pages', { n: r.fromScan.nodes + r.fromScan.blocks, p: r.leafPagesScanned }))
    if (r.unreadableItems) bits.push(t('repair_unreadable', ':n items are listed but their content is gone', { n: r.unreadableItems }))
    if (r.revivedFromOlderVersion) bits.push(t('repair_revived', ':n items were restored from an earlier version', { n: r.revivedFromOlderVersion }))
    if (r.flat) bits.push(t('repair_flat', 'The folder structure could not be walked, so folders were rebuilt from the items themselves'))
    d.textContent = bits.join('. ') + '.'
    box.append(d)
  }
}

function renderTree() {
  const list = $('#pst-folders')
  list.innerHTML = ''
  const rows = state.tree.filter(f => f.count > 0 || f.hasSub || f.recovered)
  for (const f of rows) {
    const depth = (f.path.match(/\//g) || []).length
    const item = el('button', 'pst-folder')
    item.type = 'button'
    // Indent enough to show nesting, never enough to squeeze the name out.
    item.style.paddingInlineStart = (8 + Math.min(depth, 4) * 9) + 'px'
    item.dataset.id = f.id
    if (f.id === state.folderId) item.classList.add('is-active')
    item.append(el('span', 'pst-folder-name', f.name))
    if (f.count) item.append(el('span', 'pst-folder-count', String(f.count)))
    item.addEventListener('click', () => openFolder(f.id))
    list.append(item)
  }
  if (!rows.length) list.append(el('p', 'pst-empty', t('no_folders', 'No folders could be read from this archive.')))
}

async function openFolder(id) {
  state.folderId = id
  state.offset = 0
  renderTree()
  await loadPage(0)
}

async function loadPage(offset) {
  const f = state.tree.find(x => x.id === state.folderId)
  if (!f) return
  setStatus(t('loading_folder', 'Reading folder…'))
  try {
    const r = await state.session.list(state.folderId, offset, 200)
    state.rows = r.rows
    state.offset = offset
    state.total = r.total
    renderList(f, r)
    setStatus('')
  } catch (e) {
    setStatus(t('err_folder', 'This folder could not be read: ') + e.message, 'error')
  }
}

function renderList(folder, r) {
  const box = $('#pst-list')
  box.innerHTML = ''

  const head = el('div', 'pst-list-head')
  head.append(el('h3', null, folder.name))
  const readable = r.rows.filter(x => !x.unreadable).length

  /* Declared vs readable, side by side. A folder that says 22 and yields none is
     an Outlook search folder — virtual by design — and saying so is the whole
     difference between a tool that looks broken and one that is trusted. */
  const meta = el('p', 'pst-list-meta')
  if (r.total && !r.rows.length) {
    meta.textContent = t('virtual_folder', 'This folder lists :n items but stores none of its own.', { n: r.total })
    meta.classList.add('is-note')
  } else {
    meta.textContent = t('list_meta', ':shown of :total shown', { shown: readable, total: r.total })
    if (readable < r.rows.length) meta.textContent += ' · ' + t('list_unreadable', ':n unreadable', { n: r.rows.length - readable })
  }
  head.append(meta)

  const tools = el('div', 'pst-list-tools')
  const search = el('input', 'lg-input pst-search')
  search.type = 'search'
  search.placeholder = t('filter_ph', 'Filter by subject or sender')
  search.value = state.filter
  search.addEventListener('input', () => { state.filter = search.value; paintRows() })
  tools.append(search)

  const btnFolder = el('button', 'lg-btn pst-export-folder', t('export_folder', 'Export folder'))
  btnFolder.type = 'button'
  btnFolder.addEventListener('click', () => exportFolder(folder))
  tools.append(btnFolder)

  const btnAudit = el('button', 'lg-btn lg-btn--ghost', t('audit_atts', 'Check attachments'))
  btnAudit.type = 'button'
  btnAudit.addEventListener('click', () => auditFolder(folder))
  tools.append(btnAudit)

  head.append(tools)
  box.append(head)

  const table = el('div', 'pst-rows')
  table.id = 'pst-rows'
  box.append(table)
  paintRows()

  if (r.total > 200) {
    const nav = el('div', 'pst-pager')
    const prev = el('button', 'lg-btn lg-btn--ghost', '←')
    prev.type = 'button'
    prev.disabled = state.offset === 0
    prev.addEventListener('click', () => loadPage(Math.max(0, state.offset - 200)))
    const next = el('button', 'lg-btn lg-btn--ghost', '→')
    next.type = 'button'
    next.disabled = state.offset + 200 >= r.total
    next.addEventListener('click', () => loadPage(state.offset + 200))
    nav.append(prev, el('span', 'pst-pager-info', `${state.offset + 1}–${Math.min(state.offset + 200, r.total)} / ${r.total}`), next)
    box.append(nav)
  }
}

function paintRows() {
  const table = $('#pst-rows')
  if (!table) return
  table.innerHTML = ''
  const q = state.filter.trim().toLowerCase()
  const rows = q
    ? state.rows.filter(x => (x.subject + ' ' + x.from + ' ' + x.to).toLowerCase().includes(q))
    : state.rows
  if (!rows.length) {
    table.append(el('p', 'pst-empty', q ? t('no_match', 'Nothing matches that filter.') : t('folder_empty', 'This folder holds no readable items.')))
    return
  }
  for (const m of rows) {
    const row = el('button', 'pst-row' + (m.unreadable ? ' is-unreadable' : ''))
    row.type = 'button'
    row.addEventListener('click', () => openItem(m))
    const main = el('div', 'pst-row-main')
    main.append(el('span', 'pst-row-subject', m.subject || t('no_subject', '(no subject)')))
    main.append(el('span', 'pst-row-from', m.from || m.fromAddr || ''))
    row.append(main)
    const side = el('div', 'pst-row-side')
    if (m.atts) side.append(el('span', 'pst-row-att', '📎 ' + m.atts))
    side.append(el('span', 'pst-row-date', fmtDate(m.date)))
    row.append(side)
    if (m.unreadable) row.append(el('span', 'pst-row-flag', t('row_unreadable', 'content gone')))
    table.append(row)
  }
}

async function openItem(m) {
  const pane = $('#pst-item')
  pane.hidden = false
  pane.innerHTML = ''
  pane.append(el('p', 'pst-loading', t('loading_item', 'Opening…')))
  try {
    const d = await state.session.message(state.folderId, m.index)
    pane.innerHTML = ''
    const h = el('div', 'pst-item-head')
    h.append(el('h3', null, d.subject || t('no_subject', '(no subject)')))
    const from = el('p', 'pst-item-from')
    from.append(el('b', null, d.from || d.fromAddr || ''))
    if (d.fromAddr && d.from !== d.fromAddr) from.append(document.createTextNode(' · ' + d.fromAddr))
    h.append(from)
    if (d.to) h.append(el('p', 'pst-item-to', t('to', 'To') + ': ' + d.to))
    h.append(el('p', 'pst-item-date', fmtDate(d.date)))
    pane.append(h)

    const acts = el('div', 'pst-item-actions')
    const save = el('button', 'lg-btn', t('save_item', 'Save this item'))
    save.type = 'button'
    save.addEventListener('click', () => exportItem(m))
    acts.append(save)
    pane.append(acts)

    if (d.attachments.length) {
      const list = el('div', 'pst-atts')
      list.append(el('h4', null, t('attachments', 'Attachments')))
      for (const a of d.attachments) {
        const b = el('button', 'pst-att')
        b.type = 'button'
        b.append(el('span', 'pst-att-name', a.name), el('span', 'pst-att-size', fmtBytes(a.size)))
        b.addEventListener('click', () => saveAttachment(m, a))
        list.append(b)
      }
      pane.append(list)
    }

    const body = el('div', 'pst-body')
    if (d.bodyHtml) {
      /* The HTML of a stranger's mail is rendered in a sandboxed frame with no
         scripts and no network: an archive can contain anything, including a
         tracking pixel that would announce to a third party that this mailbox
         was opened, and a person recovering their mail did not ask for that. */
      const frame = el('iframe', 'pst-body-frame')
      frame.setAttribute('sandbox', '')
      frame.setAttribute('csp', "default-src 'none'; img-src data:; style-src 'unsafe-inline'")
      frame.srcdoc = `<!doctype html><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'"><base target="_blank"><style>body{font:14px/1.5 system-ui,sans-serif;color:#111;background:#fff;margin:12px}img{max-width:100%}</style>${d.bodyHtml}`
      body.append(frame)
    } else {
      body.append(el('pre', 'pst-body-text', d.bodyText || t('no_body', '(no message text)')))
    }
    pane.append(body)

    if (d.headers) {
      const det = el('details', 'pst-headers')
      det.append(el('summary', null, t('headers', 'Original headers')))
      det.append(el('pre', null, d.headers))
      pane.append(det)
    }
  } catch (e) {
    pane.innerHTML = ''
    pane.append(el('p', 'pst-error', t('err_item', 'This item could not be opened: ') + e.message))
  }
}

function download(blob, name) {
  if (window.__downloadFile) return window.__downloadFile(blob, name)
  const u = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = u
  a.download = name
  a.click()
  setTimeout(() => URL.revokeObjectURL(u), 4000)
}

async function saveAttachment(m, a) {
  setStatus(t('saving', 'Preparing download…'))
  try {
    const r = await state.session.attachment(state.folderId, m.index, a.index)
    download(new Blob([r.bytes], { type: r.mime || 'application/octet-stream' }), r.name || a.name || 'attachment')
    setStatus('')
  } catch (e) {
    setStatus(t('err_attachment', 'That attachment could not be read: ') + e.message, 'error')
  }
}

async function exportItem(m) {
  setStatus(t('saving', 'Preparing download…'))
  try {
    const r = await state.session.call('exportItem', { folderId: state.folderId, index: m.index })
    download(new Blob([r.text], { type: r.mime }), r.filename)
    setStatus('')
  } catch (e) {
    setStatus(t('err_export', 'Export failed: ') + e.message, 'error')
  }
}

async function exportFolder(folder) {
  setStatus(t('exporting', 'Exporting folder…'))
  try {
    const r = await state.session.call('exportFolder', { folderId: folder.id })
    if (!r.files.length) { setStatus(t('nothing_to_export', 'There is nothing in this folder to export.'), 'warn'); return }
    // Папка — это пачка, а не цикл загрузок: браузер отдаёт ОДИН файл
    // на жест, остальные выбрасывает молча. К тому же все файлы звались
    // одинаково (имя папки + ext) — примитив разводит совпадения сам.
    const base = folder.name.replace(/[^\w\-. ]+/g, '_')
    const items = r.files.map((f) => ({ name: `${base}.${f.ext}`, blob: new Blob([f.text], { type: f.mime }) }))
    const BD = await (window.__ensureBatchDeliver ? window.__ensureBatchDeliver() : Promise.resolve(null))
    if (BD) await BD.deliver(items, { slug: 'pst-viewer', zipName: `${base}.zip`, skipFolder: true })
    else if (items.length === 1) download(items[0].blob, items[0].name)
    setStatus(t('exported', 'Exported :n items', { n: r.items }), 'ok')
  } catch (e) {
    setStatus(t('err_export', 'Export failed: ') + e.message, 'error')
  }
}

async function auditFolder(folder) {
  setStatus(t('auditing', 'Checking attachments…'))
  try {
    const r = await state.session.call('auditAttachments', { folderId: folder.id })
    const bad = r.rows.filter(x => ['renamed', 'zero_length', 'empty', 'unreadable'].includes(x.verdict))
    const pane = $('#pst-item')
    pane.hidden = false
    pane.innerHTML = ''
    pane.append(el('h3', null, t('audit_title', 'Attachment check')))
    pane.append(el('p', 'pst-item-date', t('audit_summary', ':n attachments checked, :b need attention', { n: r.rows.length, b: bad.length })))
    if (!bad.length) {
      pane.append(el('p', 'pst-ok', t('audit_clean', 'Every attachment contains what its name says it does.')))
    } else {
      const list = el('div', 'pst-audit')
      for (const x of bad) {
        const row = el('div', 'pst-audit-row')
        row.append(el('span', 'pst-audit-name', x.name))
        row.append(el('span', 'pst-audit-verdict', t('verdict_' + x.verdict, x.verdict)))
        if (x.detected) row.append(el('span', 'pst-audit-detected', t('audit_detected', 'looks like :f', { f: x.detected })))
        row.append(el('span', 'pst-audit-size', fmtBytes(x.size)))
        list.append(row)
      }
      pane.append(list)
    }
    setStatus('')
  } catch (e) {
    setStatus(t('err_audit', 'The check could not run: ') + e.message, 'error')
  }
}

async function deepScan() {
  setStatus(t('deep_running', 'Looking for items no folder lists…'))
  try {
    const r = await state.session.call('deepScan', {})
    const pane = $('#pst-item')
    pane.hidden = false
    pane.innerHTML = ''
    pane.append(el('h3', null, t('deep_title', 'Items found outside the folders')))
    pane.append(el('p', 'pst-item-date',
      t('deep_summary', ':loaded items read from the index, :orphans of them are not listed in any folder', { loaded: r.loaded, orphans: r.orphans })))
    const orphans = r.rows.filter(x => !x.listed)
    if (!orphans.length) {
      pane.append(el('p', 'pst-ok', t('deep_none', 'Every item in this archive is reachable through its folders — nothing is hidden.')))
    } else {
      const list = el('div', 'pst-rows')
      for (const m of orphans.slice(0, 500)) {
        const row = el('div', 'pst-row')
        const main = el('div', 'pst-row-main')
        main.append(el('span', 'pst-row-subject', m.subject || t('no_subject', '(no subject)')))
        main.append(el('span', 'pst-row-from', m.from || m.fromAddr || ''))
        row.append(main)
        row.append(el('span', 'pst-row-date', fmtDate(m.date)))
        list.append(row)
      }
      pane.append(list)
    }
    setStatus('')
  } catch (e) {
    setStatus(t('err_deep', 'The deep pass could not run: ') + e.message, 'error')
  }
}

async function open(file) {
  /* This browser cannot start the reader at all (no module workers: Firefox <
     114, Safari < 15) — say so before any allowance is reserved. */
  if (window.__moduleWorkerOk && !window.__moduleWorkerOk()) {
    try { window.__ae && window.__ae('tool_error', { slug: 'pst-viewer', error_type: 'engine_too_old_module_worker' }) } catch (_) {}
    setStatus(t('engine_too_old', 'This browser cannot open Outlook archives.'), 'error')
    return
  }
  if (state.session) state.session.close()
  state.session = new PstSession()
  state.session.addEventListener('progress', e => {
    const d = e.detail
    if (d.stage === 'repair' && d.phase === 'scan') {
      setStatus(t('repair_scanning', 'Rebuilding the index — :pct% scanned', { pct: Math.round((d.done / d.total) * 100) }), 'warn')
    } else if (d.stage === 'repair' && d.phase === 'start') {
      setStatus(t('repair_starting', 'This archive will not open normally. Rebuilding its index…'), 'warn')
    } else if (d.stage === 'flat') {
      setStatus(t('flat_starting', 'The folder structure is damaged. Rebuilding folders from the items…'), 'warn')
    } else if (d.stage === 'decode') {
      setStatus(t('decoding', 'Undoing the archive encoding…'), 'warn')
    } else if (d.stage === 'folders') {
      setStatus(t('reading_folders', 'Reading folders… :n', { n: d.folders }))
    }
  })
  state.session.addEventListener('dead', e => setStatus(e.detail.reason, 'error'))

  /* The daily run is reserved before the work and settled by its outcome.
     What is delivered here is a readable archive, so an archive that will not
     open costs nothing — refusing someone their mail AND a day's allowance for
     the same failure is the thing CLAUDE.md names outright. Keyed by file, so
     coming back to the same mailbox tomorrow's-worth of times is one run. */
  const ticket = window.__toolLimit
    ? window.__toolLimit.begin('pst-viewer', window.__toolLimit.fileKey ? window.__toolLimit.fileKey(file) : null)
    : null
  if (window.__toolLimit && !ticket) return   // the wall is shown by begin()

  setStatus(t('opening', 'Opening the archive…'))
  $('#pst-workspace').hidden = true
  try {
    const o = await state.session.open(file)
    if (ticket && ticket.commit) ticket.commit()
    /* The other six tools reported this; these two did not, so their success
       rate was invisible while their failures were not. */
    window.__aeToolComplete?.({ folders: o.totals.folders, messages: o.totals.messages })
    state.opened = o
    state.tree = o.tree
    try { window.__stage?.('complete') } catch (_) {}
  $('#pst-workspace').hidden = false

    /* Which archives arrive and how much of them we reach. The valuable number
       is the gap: an archive that reports 11 folders and 0 of 71 letters is the
       truncation case, and nobody writes in about it because they see folders
       and assume the mail is simply gone. */
    try {
      const d = o.diagnosis || {}
      const h = o.header || {}
      const r = o.repair || {}
      reportShape({
        format: 'pst',
        /* The raw pstFileType, not a label. Two wrong versions of this line
           preceded it: `d.ansi` (does not exist — reported "unicode" for every
           archive, a fabrication that reads exactly like a measurement) and
           `d.version` (real field, but the worker never forwards it; the
           redaction layer caught that one by emitting `dropped:1`).
           14/15 are ANSI, 23 Unicode, 36/37 an OST. */
        version: String(h.pstFileType ?? ''),
        size: file.size,
        ok: true,
        flags: [].concat(
          (d.problems || []).map(p => p && (p.kind || p.code || p)),
          d.versionInferred ? ['version-inferred'] : [],
          o.repaired ? ['repaired'] : [],
          r.flat ? ['folders-rebuilt'] : [],
          r.revivedFromOlderVersion ? ['revived-older'] : [],
          o.openError ? ['partial-open'] : [],
          o.decoded ? ['decoded-' + o.decoded.scheme] : [],
          o.decoded && o.decoded.relabelled ? ['crypt-mislabelled'] : [],
          h.isOst ? ['ost'] : [],
        ),
        counts: {
          folders: o.totals.folders,
          messages: o.totals.messages,
          /* `decoded` is an object or null — never a number. Reported as a flag
             below; as a count it was silently dropped and read as "0 archives
             were encrypted", which is not the same statement. */
          /* `decoded` is an object or null — never a number. Encryption is
             reported as this byte plus a flag; as a count `decoded` was
             silently dropped and read as "0 archives were encrypted", which is
             a different statement from "none needed decoding". */
          crypt: typeof h.encryptionType === 'number' ? h.encryptionType : -1,
          revived: r.revivedFromOlderVersion || 0,
        },
      })
    } catch (_) { /* telemetry must never break the tool */ }
    renderSummary(o)
    renderTree()
    const biggest = [...o.tree].sort((a, b) => b.count - a.count)[0]
    if (biggest) await openFolder(biggest.id)
    else setStatus('')
    $('#pst-deep').hidden = false
  } catch (e) {
    if (e && e.code === 'MODULE_WORKER_UNSUPPORTED') {
      // Not the archive's fault and not another tool's job: this browser cannot start the reader.
      if (ticket && ticket.abort) ticket.abort()
      try { window.__ae && window.__ae('tool_error', { slug: 'pst-viewer', error_type: 'engine_too_old_module_worker' }) } catch (_) {}
      setStatus(t('engine_too_old', 'This browser cannot open Outlook archives.'), 'error')
      return
    }
    try { window.__stage?.('error') } catch (_) {}
    suggestOtherTool(file, 'pst-viewer', document.querySelector('#pst-status'), { text: t('suggest_text'), link: t('suggest_link') })
      if (ticket && ticket.abort) ticket.abort()

    /* An archive that opened nowhere at all is the single most useful report
       this tool can produce: there is no screen to look at, so the person just
       leaves. */
    try {
      reportShape({ format: 'pst', size: file.size, ok: false, flags: ['open-failed'] })
    } catch (_) {}
    setStatus(t('err_open', 'This archive could not be opened: ') + e.message, 'error')
  }
}

export function init() {
  /* Bind to the uploader by its ID, which is the element the shared component
     dispatches on. An earlier version listened on a custom data-attribute
     instead; the attribute did reach the markup, the event did not reach the
     listener, and the page simply sat there after a file was dropped — no error
     anywhere, which is the worst way for a binding to be wrong. */
  const uploader = document.getElementById('pstDrop')
  if (uploader) {
    uploader.addEventListener('tool-uploader:file', e => { try { window.__stage?.('file_chosen', { mb: Math.round((e.detail.file?.size || 0) / 1048576) }) } catch (_) {}; open(e.detail.file) })
    uploader.addEventListener('tool-uploader:clear', () => {
      if (state.session) state.session.close()
      $('#pst-workspace').hidden = true
      $('#pst-item').hidden = true
      setStatus('')
    })
  }
  const deep = $('#pst-deep')
  if (deep) deep.addEventListener('click', deepScan)
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init)
else init()
